import type {
  ConditionalRuleDto,
  ConditionalRulesDto,
  ShopRoutingLocationRuleDto,
} from "@babackapp/private-api-client/types";

/**
 * Les règles de routage sont stockées sur la primitive partagée
 * `ConditionalRules`, mais l'interface n'expose que 4 critères, chacun avec un
 * type et un opérateur imposés. On garde donc un modèle de saisie simple
 * (`{key, value}`) et on convertit aux deux extrémités — le back valide la même
 * table de correspondance (`routing-location-rules.evaluator.ts`).
 */
export const ROUTING_CONDITION_KEYS = [
  "reasonId",
  "countryCode",
  "orderTag",
  "fulfillmentOriginLocationId",
] as const;

export type RoutingConditionKey = (typeof ROUTING_CONDITION_KEYS)[number];

export type RoutingLogicalOperator = "AND" | "OR";

/**
 * Clé de traduction du libellé de chaque critère, explicite à dessein : les clés
 * historiques ne suivent pas le nom du critère (`reasonId` porte le libellé
 * `settings.shipping.rules.reason`). Une clé interpolée depuis le nom du critère
 * tomberait juste par coïncidence sur trois d'entre eux et afficherait son propre
 * nom pour le quatrième.
 */
export const ROUTING_CONDITION_LABEL_KEYS: Record<RoutingConditionKey, string> =
  {
    reasonId: "settings.shipping.rules.reason",
    countryCode: "settings.shipping.rules.countryCode",
    orderTag: "settings.shipping.rules.orderTag",
    fulfillmentOriginLocationId:
      "settings.shipping.rules.fulfillmentOriginLocationId",
  };

export type RoutingCondition = {
  key: RoutingConditionKey;
  value: string;
};

type RoutingFieldMapping = {
  fieldId: string;
  type: ConditionalRuleDto["type"];
  operator: ConditionalRuleDto["operator"];
};

const ROUTING_FIELD_BY_KEY: Record<RoutingConditionKey, RoutingFieldMapping> = {
  reasonId: {
    fieldId: "returnReasonIds",
    type: "ARRAY",
    operator: "CONTAINS",
  },
  countryCode: {
    fieldId: "customerShippingCountryCode",
    type: "STRING",
    operator: "EQ",
  },
  orderTag: {
    fieldId: "orderTags",
    type: "ARRAY",
    operator: "CONTAINS",
  },
  fulfillmentOriginLocationId: {
    fieldId: "fulfillmentLocationIds",
    type: "ARRAY",
    operator: "CONTAINS",
  },
};

const ROUTING_KEY_BY_FIELD_ID = Object.fromEntries(
  ROUTING_CONDITION_KEYS.map((key) => [
    ROUTING_FIELD_BY_KEY[key].fieldId,
    key,
  ]),
) as Record<string, RoutingConditionKey>;

/**
 * Conditions d'une règle ramenées au modèle de saisie. Retourne un tableau vide
 * quand la règle n'a pas de conditions — cas des règles historiques que la
 * migration n'a pas su transposer, laissées inertes mais visibles.
 */
export const getRoutingRuleConditions = (
  rule: Pick<ShopRoutingLocationRuleDto, "conditions">,
): RoutingCondition[] =>
  (rule.conditions?.rules ?? []).flatMap((condition) => {
    const key = ROUTING_KEY_BY_FIELD_ID[condition.id];

    if (!key || typeof condition.value !== "string") return [];

    return [{ key, value: condition.value }];
  });

export const getRoutingRuleLogicalOperator = (
  rule: Pick<ShopRoutingLocationRuleDto, "conditions">,
): RoutingLogicalOperator => (rule.conditions?.operator === "OR" ? "OR" : "AND");

export const toConditionalRules = (
  conditions: RoutingCondition[],
  logicalOperator: RoutingLogicalOperator,
): ConditionalRulesDto => ({
  operator: logicalOperator,
  rules: conditions.map((condition) => {
    const mapping = ROUTING_FIELD_BY_KEY[condition.key];

    return {
      id: mapping.fieldId,
      type: mapping.type,
      operator: mapping.operator,
      value: condition.value,
    };
  }),
});

export const createEmptyRoutingCondition = (
  defaultReasonId?: string,
): RoutingCondition => ({
  key: "reasonId",
  value: defaultReasonId ?? "",
});
